import { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Package, Upload, Download, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ImportModal from "@/components/ImportModal";
import ConfirmDelete from "@/components/ConfirmDelete";
import { deleteWithUndo } from "@/lib/deleteWithUndo";
import Pagination from "@/components/Pagination";

const empty = { category: "", model: "", brand: "", variant: "", price: 0, stock: 0, description: "", notes: "", image_url: "", custom: {} };

// Neon placeholder used when a product has no image.
// Same visual language reused inside CallingDesk equipment cards.
export function ProductImageOrPlaceholder({ product, size = "md" }) {
  const sizeCls = size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm";
  const label = product?.brand
    ? `${product.brand} ${product.model || ""}`.trim()
    : (product?.name || product?.category || "Machine");
  if (product?.image_url) {
    return <img src={product.image_url} alt={label} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-[#0B0F0C] border border-[#39FF14]/20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#39FF14]/5 via-transparent to-transparent" />
      <Package size={size === "sm" ? 16 : 28} className="text-[#39FF14]/70 relative" />
      <div className={`${sizeCls} font-heading font-bold text-[#39FF14] uppercase tracking-wider text-center px-2 relative leading-tight`}
        style={{ textShadow: "0 0 8px rgba(57,255,20,0.35)" }}>
        {label}
      </div>
    </div>
  );
}

export default function Products() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [canManageImages, setCanManageImages] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(24);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, total_pages: 0, page_size: 24 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [fields, setFields] = useState([]);
  const [meta, setMeta] = useState({ categories: [], brands: [], by_category: {}, by_brand: {} });
  const [brandMaster, setBrandMaster] = useState([]);       // Active brand-master rows (id, name)
  const [categoryMaster, setCategoryMaster] = useState([]); // Active category-master rows (id, name)
  const [newBrandMode, setNewBrandMode] = useState(false);
  const [newBrandDraft, setNewBrandDraft] = useState("");
  const [newCatMode, setNewCatMode] = useState(false);
  const [newCatDraft, setNewCatDraft] = useState("");
  const [selectedCats, setSelectedCats] = useState([]);   // multi-select category checkbox filter
  const [selectedBrands, setSelectedBrands] = useState([]); // multi-select brand checkbox filter
  const [uploading, setUploading] = useState(false);

  // Load master lists (brand + category) — the authoritative dropdown source.
  const loadMasters = async () => {
    try {
      const [b, c] = await Promise.all([api.get("/brands"), api.get("/categories")]);
      setBrandMaster(b.data || []);
      setCategoryMaster(c.data || []);
    } catch { /* non-fatal — dropdown will fall back to meta */ }
  };

  // Derived Product Name — Brand + Model (+ Variant if present). Never
  // independently editable; regenerates whenever brand/model/variant changes.
  const derivedName = [form.brand, form.model].filter(Boolean).join(" ").trim() + (form.variant ? ` (${form.variant})` : "");

  const load = async (query = q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      // Iter4 pagination: multi-select category/brand filters are applied SERVER-SIDE
      // so that pagination reflects the filtered total and the grid shows the actual
      // matching page. Client-side filtering on the current page would hide products
      // that live on other pages (root cause of the empty-grid bug).
      if (selectedCats.length) params.set("categories", selectedCats.join(","));
      if (selectedBrands.length) params.set("brands", selectedBrands.join(","));
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/products?${params.toString()}`);
      const items = Array.isArray(data) ? data : (data?.items || []);
      setRows(items);
      setPageInfo({
        page: data?.page ?? 1,
        page_size: data?.page_size ?? pageSize,
        total: data?.total ?? items.length,
        total_pages: data?.total_pages ?? 1,
      });
    } finally { setLoading(false); }
  };
  const loadMeta = async () => {
    try {
      const { data } = await api.get("/products/master/meta");
      setMeta(data || { categories: [], brands: [], by_category: {}, by_brand: {} });
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load(q);
    // eslint-disable-next-line
  }, [q, page, selectedCats, selectedBrands]);

  // Reset to page 1 when query or any filter changes.
  useEffect(() => { setPage(1); }, [q, selectedCats, selectedBrands]);

  useEffect(() => {
    loadMeta();
    loadMasters();
    api.get("/fields/product").then((r) => setFields(r.data)).catch(() => {});
    api.get("/permissions/mine").then((r) => {
      const perms = new Set(r.data?.permissions || []);
      setCanManageImages(perms.has("products.manage_images"));
    }).catch(() => {});
  }, []);

  // Iter4: filter total now comes directly from the server-side `/api/products` envelope
  // (`pageInfo.total`) — see load(). Removed the separate `/products/master/filter-count`
  // POST, which was redundant and could disagree with the actual product query.

  // Iter4: filters are applied server-side (see load()), so `rows` already
  // contains only the matching products for the current page. `pageInfo.total`
  // is the canonical filtered total.
  const visible = rows;

  const toggleCat = (c) => setSelectedCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  const toggleBrand = (b) => setSelectedBrands((prev) => prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]);
  const clearAll = () => { setSelectedCats([]); setSelectedBrands([]); };
  const activeFilterCount = selectedCats.length + selectedBrands.length;

  const submit = async (e) => {
    e.preventDefault();
    // Product Name is DERIVED — never sent. Backend regenerates from brand+model+variant.
    if (!form.brand?.trim() || !form.model?.trim() || !form.category?.trim()) {
      toast.error("Category, Brand, and Model are required");
      return;
    }
    const payload = {
      category: form.category.trim(),
      brand: form.brand.trim(),
      model: form.model.trim(),
      variant: (form.variant || "").trim(),
      description: form.description || "",
      notes: form.notes || "",
      image_url: form.image_url || "",
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock) || 0,
      custom: form.custom || {},
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
      return;
    }
    setOpen(false); setEditing(null); setForm(empty);
    setNewBrandMode(false); setNewBrandDraft(""); setNewCatMode(false); setNewCatDraft("");
    loadMasters(); loadMeta(); load(q);
  };

  const edit = (p) => {
    setEditing(p);
    // Never seed the derived `name` — form only holds identity fields.
    setForm({ ...empty, category: p.category || "", brand: p.brand || "", model: p.model || "",
              variant: p.variant || "", price: p.price ?? 0, stock: p.stock ?? 0,
              description: p.description || "", notes: p.notes || "", image_url: p.image_url || "",
              custom: p.custom || {} });
    setNewBrandMode(false); setNewCatMode(false);
    setOpen(true);
  };
  const del = (p) =>
    deleteWithUndo({
      deletePath: `/products/${p.id}`,
      restorePath: `/products/${p.id}/restore`,
      label: `${p.brand} ${p.model}`.trim() || p.name,
      entityLabel: "product",
      onChange: () => load(q),
    });

  return (
    <div className="p-8 lg:p-10 max-w-[1600px]">
      <PageHeader
        title="Product Master"
        subtitle="TZAT's equipment catalogue — Category · Brand · Model. Inventory quantity is tracked separately (future release)."
        exportEntity="products"
        testid="page-products-header"
        action={
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                try {
                  const resp = await api.get("/product-master/import-template", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                  const a = document.createElement("a");
                  a.href = url; a.download = "TZAT_Product_Master_Template.xlsx";
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) { toast.error("Failed to download template"); }
              }}
              variant="outline"
              className="border-[#3A4640]"
              data-testid="download-product-template-btn"
            >
              <Download size={14} className="mr-2" /> Download Template
            </Button>
            <Button onClick={() => setImportOpen(true)} variant="outline" data-testid="import-products-btn" className="border-[#3A4640]">
              <Upload size={14} className="mr-2" /> Import
            </Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); setNewBrandMode(false); setNewBrandDraft(""); setNewCatMode(false); setNewCatDraft(""); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
                <Plus size={16} className="mr-2" /> New Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                {/* Category (master dropdown + "+ New Category") */}
                <div>
                  <Label>Category*</Label>
                  {newCatMode ? (
                    <div className="flex gap-2">
                      <Input
                        required autoFocus data-testid="product-newcategory-input"
                        placeholder="New category name"
                        value={newCatDraft}
                        onChange={(e) => { setNewCatDraft(e.target.value); setForm({ ...form, category: e.target.value }); }}
                      />
                      <button type="button" className="text-xs px-2 text-[#8A8F98] hover:text-[#39FF14]" onClick={() => { setNewCatMode(false); setNewCatDraft(""); setForm({ ...form, category: "" }); }}>Cancel</button>
                    </div>
                  ) : (
                    <Select
                      value={form.category}
                      onValueChange={(v) => {
                        if (v === "__new__") { setNewCatMode(true); setForm({ ...form, category: "" }); return; }
                        setForm({ ...form, category: v });
                      }}
                    >
                      <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__new__" data-testid="product-category-new-option">+ New Category</SelectItem>
                        {(categoryMaster.length ? categoryMaster.map((c) => c.name) : (meta.categories || [])).map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {/* Brand (master dropdown + "+ New Brand") */}
                <div>
                  <Label>Brand*</Label>
                  {newBrandMode ? (
                    <div className="flex gap-2">
                      <Input
                        required autoFocus data-testid="product-newbrand-input"
                        placeholder="New brand name"
                        value={newBrandDraft}
                        onChange={(e) => { setNewBrandDraft(e.target.value); setForm({ ...form, brand: e.target.value }); }}
                      />
                      <button type="button" className="text-xs px-2 text-[#8A8F98] hover:text-[#39FF14]" onClick={() => { setNewBrandMode(false); setNewBrandDraft(""); setForm({ ...form, brand: "" }); }}>Cancel</button>
                    </div>
                  ) : (
                    <Select
                      value={form.brand}
                      onValueChange={(v) => {
                        if (v === "__new__") { setNewBrandMode(true); setForm({ ...form, brand: "" }); return; }
                        setForm({ ...form, brand: v });
                      }}
                    >
                      <SelectTrigger data-testid="product-brand-select"><SelectValue placeholder="Select brand" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__new__" data-testid="product-brand-new-option">+ New Brand</SelectItem>
                        {(brandMaster.length ? brandMaster.map((b) => b.name) : (meta.brands || [])).map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div><Label>Model*</Label><Input required placeholder="e.g. FS 230" data-testid="product-model-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
                <div><Label>Variant</Label><Input placeholder="e.g. 6 HP / Diesel" data-testid="product-variant-input" value={form.variant || ""} onChange={(e) => setForm({ ...form, variant: e.target.value })} /></div>
                {/* Derived Product Name preview — read-only, always regenerated from Brand + Model (+ Variant). */}
                <div className="col-span-2">
                  <Label className="text-[#8A8F98]">Product Name (auto-generated)</Label>
                  <div
                    data-testid="product-name-derived"
                    className="mt-1 px-3 py-2 border border-[#3A4640] rounded bg-[#0B0F0C] text-[#39FF14] font-heading font-bold min-h-[38px] flex items-center"
                  >
                    {derivedName || <span className="text-[#8A8F98] font-normal">— fill Brand and Model —</span>}
                  </div>
                </div>
                <div><Label>Price*</Label><Input required type="number" data-testid="product-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
                <div><Label>Stock*</Label><Input required type="number" data-testid="product-stock-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Product Image</Label>
                  {!editing ? (
                    <div className="mt-1.5 text-xs text-[#8A8F98] p-3 border border-dashed border-[#3A4640] rounded bg-[#0B0F0C]">
                      Save the product first, then re-open it to upload an image.
                    </div>
                  ) : !canManageImages ? (
                    <div className="mt-1.5 text-xs text-[#8A8F98] p-3 border border-dashed border-[#3A4640] rounded bg-[#0B0F0C]">
                      You don't have permission to manage product images. Ask an admin.
                    </div>
                  ) : (
                    <div className="mt-1.5 flex items-center gap-4">
                      <div className="w-24 h-24 rounded-md overflow-hidden border border-[#3A4640] shrink-0" data-testid="product-image-preview">
                        <ProductImageOrPlaceholder product={form} size="sm" />
                      </div>
                      <div className="flex-1 flex flex-col gap-2">
                        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#3A4640] bg-[#0B0F0C] text-sm text-[#F8F9FA] hover:border-[#39FF14]/40 cursor-pointer w-fit"
                          data-testid="product-image-upload-btn">
                          {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                          {form.image_url ? "Replace image" : "Upload image"}
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                            disabled={uploading}
                            onChange={async (e) => {
                              const f = e.target.files?.[0]; if (!f) return;
                              if (f.size > 5 * 1024 * 1024) { toast.error("Image exceeds 5 MB limit"); return; }
                              const fd = new FormData(); fd.append("file", f);
                              setUploading(true);
                              try {
                                const { data } = await api.post(`/products/${editing.id}/image`, fd,
                                  { headers: { "Content-Type": "multipart/form-data" } });
                                setForm((s) => ({ ...s, image_url: data.image_url }));
                                toast.success("Image uploaded");
                                load(q);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Upload failed");
                              } finally {
                                setUploading(false);
                                e.target.value = "";
                              }
                            }} />
                        </label>
                        {form.image_url && (
                          <button type="button" onClick={async () => {
                            if (!window.confirm("Remove product image?")) return;
                            try {
                              await api.delete(`/products/${editing.id}/image`);
                              setForm((s) => ({ ...s, image_url: "" }));
                              toast.success("Image removed");
                              load(q);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Remove failed");
                            }
                          }} className="inline-flex items-center gap-2 text-xs text-red-400 hover:text-red-300 w-fit"
                            data-testid="product-image-remove-btn">
                            <X size={12} /> Remove image
                          </button>
                        )}
                        <div className="text-[11px] text-[#8A8F98]">JPG · PNG · WebP · up to 5 MB. 1:1 square renders best.</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                {fields.length > 0 && (
                  <div className="col-span-2 border-t border-[#3A4640] pt-4 mt-2">
                    <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading font-semibold mb-3">Custom Fields</div>
                    <div className="grid grid-cols-2 gap-4">
                      {fields.map((f) => {
                        const val = form.custom?.[f.key] ?? "";
                        const setVal = (v) => setForm({ ...form, custom: { ...(form.custom || {}), [f.key]: v } });
                        return (
                          <div key={f.id}>
                            <Label>{f.label}{f.required && "*"}</Label>
                            {f.type === "dropdown" ? (
                              <select
                                required={f.required}
                                value={val}
                                onChange={(e) => setVal(e.target.value)}
                                data-testid={`product-custom-${f.key}-select`}
                                className="mt-1.5 w-full h-9 px-3 py-1 rounded-md border border-[#3A4640] text-sm bg-[#121815]"
                              >
                                <option value="">Select…</option>
                                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <Input
                                type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                                required={f.required}
                                data-testid={`product-custom-${f.key}-input`}
                                value={val}
                                onChange={(e) => setVal(e.target.value)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid="product-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
          <Input data-testid="product-search-input" className="pl-9" placeholder="Search brand, model, category…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {/* Category + Brand checkbox filters */}
      {((meta.categories?.length || 0) > 0 || (meta.brands?.length || 0) > 0) && (
        <div className="tzat-card p-4 mb-4" data-testid="product-filters">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading font-semibold">Filters</div>
            <div className="flex items-center gap-3">
              <span data-testid="product-filter-result-count" className="text-xs flex items-center gap-1.5">
                {activeFilterCount === 0 ? (
                  <span className="text-[#8A8F98]">Showing all <span className="text-[#F8F9FA] font-heading font-semibold">{pageInfo.total}</span> products</span>
                ) : (
                  <>
                    <span
                      className="font-heading font-bold text-[#39FF14] text-base leading-none"
                      style={{ textShadow: "0 0 8px rgba(57,255,20,0.5)" }}>
                      {pageInfo.total}
                    </span>
                    <span className="text-[#8A8F98]">products match · showing <span className="text-[#F8F9FA]">{visible.length}</span> on this page</span>
                  </>
                )}
              </span>
              {activeFilterCount > 0 && (
                <button onClick={clearAll} data-testid="product-filter-clear-all"
                  className="text-xs text-[#39FF14] hover:underline font-semibold">Clear all ({activeFilterCount})</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Category */}
            {(meta.categories?.length || 0) > 0 && (
              <div data-testid="product-category-filter">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading font-semibold flex items-center gap-2">
                    Categories
                    {selectedCats.length > 0 && <span className="text-[10px] text-[#39FF14]">({selectedCats.length})</span>}
                  </div>
                  {selectedCats.length > 0 && (
                    <button onClick={() => setSelectedCats([])} data-testid="product-category-clear"
                      className="text-[11px] text-[#8A8F98] hover:text-[#F8F9FA]">Reset</button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[260px] overflow-auto pr-1">
                  {meta.categories.map((c) => {
                    const checked = selectedCats.includes(c);
                    const count = meta.by_category?.[c] || 0;
                    return (
                      <label key={c} data-testid={`product-cat-chk-${c}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-[10px] border cursor-pointer transition-colors text-sm ${
                          checked ? "border-[#39FF14]/50 bg-[#39FF14]/5 text-[#F8F9FA]" : "border-[#3A4640] bg-[#0B0F0C] text-[#8A8F98] hover:text-[#F8F9FA]"
                        }`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleCat(c)} className="w-4 h-4 accent-[#39FF14]" />
                        <span className="flex-1 truncate">{c}</span>
                        <span className="text-[10px] font-mono text-[#8A8F98]">{count}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Brand */}
            {(meta.brands?.length || 0) > 0 && (
              <div data-testid="product-brand-filter">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading font-semibold flex items-center gap-2">
                    Brands
                    {selectedBrands.length > 0 && <span className="text-[10px] text-[#39FF14]">({selectedBrands.length})</span>}
                  </div>
                  {selectedBrands.length > 0 && (
                    <button onClick={() => setSelectedBrands([])} data-testid="product-brand-clear"
                      className="text-[11px] text-[#8A8F98] hover:text-[#F8F9FA]">Reset</button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[260px] overflow-auto pr-1">
                  {meta.brands.map((b) => {
                    const checked = selectedBrands.includes(b);
                    const count = meta.by_brand?.[b] || 0;
                    return (
                      <label key={b} data-testid={`product-brand-chk-${b}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-[10px] border cursor-pointer transition-colors text-sm ${
                          checked ? "border-[#39FF14]/50 bg-[#39FF14]/5 text-[#F8F9FA]" : "border-[#3A4640] bg-[#0B0F0C] text-[#8A8F98] hover:text-[#F8F9FA]"
                        }`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleBrand(b)} className="w-4 h-4 accent-[#39FF14]" />
                        <span className="flex-1 truncate">{b}</span>
                        <span className="text-[10px] font-mono text-[#8A8F98]">{count}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-[#121815] border border-[#3A4640] rounded-md p-12 text-center text-[#8A8F98]" data-testid="products-empty-state">
          <Package className="mx-auto mb-3" />
          {rows.length === 0 ? "No products yet. Add machinery to your inventory." : "No products match the selected filters."}
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
          {visible.map((p) => (
            <div key={p.id} className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm overflow-hidden" data-testid={`product-card-${p.id}`}>
              <div className="aspect-square bg-[#0B0F0C] relative overflow-hidden">
                <ProductImageOrPlaceholder product={p} size="md" />
                <div className="absolute top-2 left-2 bg-[#39FF14] text-[#0B0F0C] text-xs px-2 py-1 rounded font-heading font-medium">{p.category}</div>
                {p.stock <= 2 && (
                  <div className="absolute top-2 right-2 bg-[#DC2626] text-white text-xs px-2 py-1 rounded font-heading font-medium">
                    {p.stock === 0 ? "Out of stock" : `${p.stock} left`}
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="text-xs text-[#8A8F98] uppercase font-heading font-semibold tracking-wider">{p.brand}</div>
                <div className="font-heading font-bold text-[#F8F9FA] mt-1">{p.model}</div>
                <div className="text-sm text-[#8A8F98] mt-0.5">{p.name}</div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#3A4640]">
                  <div>
                    <div className="font-heading text-xl font-extrabold text-[#39FF14]">{formatCurrency(p.price)}</div>
                    <div className="text-xs text-[#8A8F98]">Stock: {p.stock}</div>
                  </div>
                  <div>
                    <button onClick={() => edit(p)} className="p-2 hover:bg-[#121815] rounded" data-testid={`edit-product-${p.id}`}><Pencil size={14} /></button>
                    {isAdmin && (
                      <ConfirmDelete
                        itemLabel={`${p.brand} ${p.model}`.trim() || p.name}
                        entityLabel="product"
                        onConfirm={() => del(p)}
                        testid={`product-${p.id}`}
                      >
                        <button className="p-2 hover:bg-red-500/10 text-red-600 rounded" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></button>
                      </ConfirmDelete>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-[#121815] border border-[#3A4640] rounded-md overflow-hidden">
          <Pagination
            page={pageInfo.page}
            totalPages={pageInfo.total_pages}
            total={pageInfo.total}
            pageSize={pageInfo.page_size}
            onPageChange={setPage}
            loading={loading}
            testid="products-pagination"
          />
        </div>
        </>
      )}

      <ImportModal entity="products" open={importOpen} onOpenChange={setImportOpen} onImported={() => load(q)} />
    </div>
  );
}
